import { usePathname } from 'next/navigation';
import { FC, ReactNode, useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { mobileTabs } from '@/components/navigation/platmap-layout';
import MapBox from '@/components/page-components/plat-map/mapbox';
import { antiScreenshotElementProps } from '@/constants/constants';
import {
  COMPS_URL,
  INSIGHT_URL,
  LISTINGS_URL,
  PLAT_MAP_URL_LANDING,
} from '@/constants/urls';
import DownloadAppCTA from '@/features/insightsCoverage/downloadAppCta';
import { useInsightsCoverageContext } from '@/features/insightsCoverage/provider';
import { OldSelectionsPanel } from '@/features/selectionsPanelOld/selectionsPanel';
import { useDoesPathInclude } from '@/hooks/useDoesPathInclude';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

import SeoExpansion from '../../insightsCoverage/seoExpansion';
import BottomNavigation from '../bottom-navigation';
import { useNavigationContext } from '../provider';
import { MobileSideNav } from './mobileSideNav';

type Props = {
  children: ReactNode;
  isMobilePanelOpen: boolean;
};

const quarterPanelInsights = [INSIGHT_URL, PLAT_MAP_URL_LANDING];

export const MobileNavigationWrapper: FC<Props> = ({
  children,
  isMobilePanelOpen,
}) => {
  const pathname = usePathname();
  const { mapInstance: map } = useMapboxContext();
  const { expandMobilePanel, setCurrentMobileTab, currentMobileTab } =
    useNavigationContext();

  const { insightsCoverage } = useInsightsCoverageContext();

  const {
    title,
    body,
    stateCounties,
    countyInfo,
    stateInfo,
    countiesWithListings,
  } = insightsCoverage;

  const adjacentCounties = useMemo(() => {
    return countyInfo?.adjacent_counties;
  }, [countyInfo?.adjacent_counties]);

  const isOnBaseMap = useDoesPathInclude(PLAT_MAP_URL_LANDING);
  const isOnListingsPage = useDoesPathInclude(LISTINGS_URL);
  const isOnCompsPage = useDoesPathInclude(COMPS_URL);

  useEffect(() => {
    if (isOnListingsPage || isOnCompsPage)
      setCurrentMobileTab(mobileTabs[0].value);
  }, [isOnCompsPage, isOnListingsPage, setCurrentMobileTab]);

  const panelHeightClass = useMemo(() => {
    const isQuarterPanel = quarterPanelInsights.some((url) =>
      pathname?.includes(url)
    );

    if (isQuarterPanel && expandMobilePanel) return 'h-[255px]';
    if (isQuarterPanel) return 'h-[56px]';
    if (expandMobilePanel) return 'h-full';

    return 'h-[50%]';
  }, [expandMobilePanel, pathname]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="absolute top-0 h-full w-full p-0 scrollbar-hide">
        {!currentMobileTab && (
          <>
            <DownloadAppCTA />
            <SeoExpansion
              adjacentCounties={adjacentCounties}
              body={body}
              className="pb-[100px]"
              countiesWithListings={countiesWithListings ?? []}
              countyInfo={countyInfo}
              serverSide={false}
              stateCounties={stateCounties}
              stateInfo={stateInfo}
              title={title}
            />
          </>
        )}

        <OldSelectionsPanel />
      </div>
      <div {...antiScreenshotElementProps} className="absolute bottom-0">
        <BottomNavigation map={map} />
      </div>
      <MapBox />
      {isMobilePanelOpen && (
        <div
          {...antiScreenshotElementProps}
          className={twMerge(
            'absolute bottom-[0px] z-[201] flex w-full flex-col overflow-scroll bg-white transition-all scrollbar-hide',
            panelHeightClass,
            !expandMobilePanel && 'overflow-hidden'
          )}
        >
          <div className={twMerge('mb-6 flex h-full flex-col')}>
            {isOnBaseMap && <MobileSideNav />}
            {children}
          </div>
        </div>
      )}
      {!isMobilePanelOpen && (
        <div {...antiScreenshotElementProps} className="hidden">
          {children}
        </div>
      )}
    </div>
  );
};
